import React, { useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import { Alert } from "antd";
import PhoneInput, { isPossiblePhoneNumber, parsePhoneNumber, getCountryCallingCode } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import t from "../service/translate.service";
import { phone } from "../service/validate";
import { useTheme } from "../providers/ThemeProvider";
import LoadingPageOverlay from "./LoadingPageOverlay";

const ContactDetailsModal = ({
  contractStore,
  isOpen,
  onConfirm,
  onCancel,
  source = 'contract_overview',
  asStep = false, // When true, render as inline step (e.g. new SR flow) instead of modal
}) => {
  const { magnifyText } = useTheme();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [originalData, setOriginalData] = useState({ email: "", mobile: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submittedChangeType, setSubmittedChangeType] = useState(null); // 'email' | 'mobile' for success message
  const [error, setError] = useState("");

  const tenants = contractStore.tenants || [];
  const selectedContract = contractStore.selectedContract;
  const contractNumber = selectedContract?.contractNumber;
  const aimsContactId = selectedContract?.aimsContactId;

  const currentUserTenant = tenants.find(
    (tenant) => tenant.partner === aimsContactId
  );

  const isActive = isOpen || asStep;

  useEffect(() => {
    if (isActive && currentUserTenant) {
      setOriginalData({
        email: currentUserTenant.email || "",
        mobile: currentUserTenant.mobile || "",
        firstName: currentUserTenant.firstName || "",
        lastName: currentUserTenant.lastName || "",
        partner: currentUserTenant.partner || "",
      });
    }
  }, [isActive, currentUserTenant]);

  useEffect(() => {
    if (isActive && tenants.length === 0 && contractNumber) {
      setLoading(true);
      contractStore.getContractDetails(contractNumber).finally(() => {
        setLoading(false);
      });
    }
  }, [isActive, contractNumber, tenants.length, contractStore]);

  useEffect(() => {
    if (!isOpen && !asStep) {
      setEditMode(null);
      setNewValue("");
      setSubmitted(false);
      setSubmittedChangeType(null);
      setError("");
    }
  }, [isOpen, asStep]);

  if (!asStep && !isOpen) return null;

  const handleChangeClick = (field) => {
    setEditMode(field);
    setNewValue(originalData[field] || "");
  };

  const handleBackToView = () => {
    setEditMode(null);
    setNewValue("");
  };

  const handleSubmit = async () => {
    if (!editMode || newValue === originalData[editMode]) {
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const formatMobileForSap = (internationalNumber) => {
        const parsed = parsePhoneNumber(internationalNumber);
        if (!parsed) return internationalNumber;
        return `${parsed.country}${parsed.nationalNumber}`;
      };

      const isEditingMobile = editMode === 'mobile';
      const newMobileE164 = isEditingMobile ? newValue : originalData.mobile
      const newMobileSap = isEditingMobile ? formatMobileForSap(newValue) : (originalData.mobile?.startsWith('+') ? formatMobileForSap(originalData.mobile) : originalData.mobile);
      const oldMobileE164 = originalData.mobile
      const oldMobileSap = originalData.mobile?.startsWith('+') ? formatMobileForSap(originalData.mobile) : originalData.mobile;

      const payload = {
        contractNumber: contractNumber,
        partnerId: originalData.partner,
        appId: selectedContract?.id,
        newEmail: editMode === 'email' ? newValue : originalData.email,
        newMobile: newMobileE164,
        newMobileSap: newMobileSap,
        oldEmail: originalData.email,
        oldMobile: oldMobileE164,
        oldMobileSap: oldMobileSap,
        source: source,
      };

      const result = await contractStore.updateContactDetails(payload);
      setSubmittedChangeType(result?.emailChanged ? 'email' : 'mobile');
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to update contact details:', err);
      const errorMessage = err?.message || err?.response?.data?.message || '';
      
      let displayError = t.CONTACT_DETAILS_UPDATE_ERROR || 'Failed to update contact details. Please try again.';
      
      if (errorMessage.includes('sms_send_failed:')) {
        const mobile = errorMessage.split(':')[1];
        displayError = `${t.CONTACT_DETAILS_SMS_FAILED} ${mobile}`;
      } else if (errorMessage.includes('email_send_failed:')) {
        const email = errorMessage.split(':')[1];
        displayError = `${t.CONTACT_DETAILS_EMAIL_FAILED} ${email}`;
      }
      
      setError(displayError);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setEditMode(null);
    onCancel();
  };

  const handleSuccessClose = () => {
    setSubmitted(false);
    setEditMode(null);
    onConfirm();
  };

  const fontSizes = {
    title: `${magnifyText(20)}px`,
    text: `${magnifyText(14)}px`,
    button: `${magnifyText(16)}px`,
  };

  const confirmLabel = asStep ? (t['SR_STEP_BUTTON_NEXT'] || 'Next') : (t.CONTACT_DETAILS_CONFIRM || 'Confirm');
  const cancelLabel = asStep ? (t['SR_STEP_BUTTON_BACK'] || 'Back') : (t.FORM_CANCEL || 'Cancel');

  if (loading) {
    return <LoadingPageOverlay />;
  }

  const successContentModal = (
    <div className="contact-details-modal">
      <div className="contact-details-modal-success-container">
        <div className="contact-details-modal-success-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 className="contact-details-modal-title" style={{ fontSize: fontSizes.title }}>
          {t.CONTACT_DETAILS_SUCCESS_TITLE}
        </h2>
        <p className="contact-details-modal-success-message" style={{ fontSize: fontSizes.text }}>
          {submittedChangeType === 'mobile'
            ? (t.CONTACT_DETAILS_SUCCESS_MESSAGE_SMS || t.CONTACT_DETAILS_SUCCESS_MESSAGE)
            : t.CONTACT_DETAILS_SUCCESS_MESSAGE}
        </p>
        <button 
          className="contact-details-modal-primary-button" 
          style={{ fontSize: fontSizes.button }}
          onClick={handleSuccessClose}
        >
          {asStep ? confirmLabel : 'OK'}
        </button>
      </div>
    </div>
  );

  const successContentStep = (
    <>
      <div className="step-header">
        <h2>{t.CONTACT_DETAILS_SUCCESS_TITLE}</h2>
        <p className="contact-details-step-message">
          {submittedChangeType === 'mobile'
            ? (t.CONTACT_DETAILS_SUCCESS_MESSAGE_SMS || t.CONTACT_DETAILS_SUCCESS_MESSAGE)
            : t.CONTACT_DETAILS_SUCCESS_MESSAGE}
        </p>
      </div>
      <div className="contact-details-step-success-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div className="contract-step-actions contact-details-step-actions-single">
        <button type="button" className="button primary" onClick={handleSuccessClose}>
          {confirmLabel}
        </button>
      </div>
    </>
  );

  if (submitted) {
    return asStep ? (
      <div className="step-container contact-details-step">{successContentStep}</div>
    ) : (
      <div className="contact-details-modal-overlay" role="dialog" aria-modal="true">{successContentModal}</div>
    );
  }

  if (editMode) {
    const fieldLabel = editMode === 'email' ? t.USER_EMAIL : t.CONTACT_DETAILS_MOBILE;
    const inputType = editMode === 'email' ? 'email' : 'tel';
    const placeholder = editMode === 'email'
      ? (t.CONTACT_DETAILS_NEW_EMAIL || 'Enter new email address')
      : (t.CONTACT_DETAILS_NEW_MOBILE || 'Enter new mobile number');

    const isMobileEdit = editMode === 'mobile';
    const phoneRegexError = isMobileEdit && newValue ? phone(newValue, t.CONTACT_DETAILS_MOBILE) : null;
    const mobileValid = !isMobileEdit || (newValue && isPossiblePhoneNumber(newValue));
    const sameMobile = isMobileEdit && newValue && originalData.mobile
      ? (() => {
          try {
            const parsedNew = parsePhoneNumber(newValue);
            const parsedOrig = parsePhoneNumber(originalData.mobile);
 
            return parsedNew && parsedOrig && parsedNew.number === parsedOrig.number;
          } catch (_) {
            return newValue === originalData.mobile;
          }
        })()
      : false;

    const isUnchanged = editMode === 'email' ? newValue === originalData.email : sameMobile;
    const submitDisabled = !newValue || !mobileValid || isUnchanged || !!phoneRegexError;

    const editContentModal = (
      <div className="contact-details-modal">
        <h2 className="contact-details-modal-title" style={{ fontSize: fontSizes.title }}>
          {editMode === 'email' ? t.CONTACT_DETAILS_CHANGE_EMAIL : t.CONTACT_DETAILS_CHANGE_MOBILE}
        </h2>
        {error && (
          <Alert
            message={error}
            type="error"
            role="alert"
            aria-live="assertive"
            showIcon
            style={{ marginBottom: 16, fontSize: fontSizes.text }}
          />
        )}
        <div className="contact-details-modal-edit-section">
          <label className="contact-details-modal-edit-label" style={{ fontSize: fontSizes.text }}>{fieldLabel}</label>
          {isMobileEdit ? (
            <>
              <PhoneInput
                international
                defaultCountry="DE"
                value={newValue}
                onChange={(val) => setNewValue(val || '')}
                placeholder={placeholder}
                className={"contact-details-phone-input" + (phoneRegexError ? " contact-details-phone-input-error" : "")}
                style={{ fontSize: fontSizes.text }}
                numberInputProps={{
                  className: "contact-details-modal-input",
                  style: { fontSize: fontSizes.text },
                  autoFocus: true,
                }}
              />
              {phoneRegexError && (
                <p className="contact-details-modal-error contact-details-modal-field-error" style={{ fontSize: fontSizes.text }}>{phoneRegexError}</p>
              )}
            </>
          ) : (
            <input
              type={inputType}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={placeholder}
              className="contact-details-modal-input"
              style={{ fontSize: fontSizes.text }}
              autoFocus
            />
          )}
        </div>
        <div className="contact-details-modal-button-container">
          <button type="button" className="contact-details-modal-secondary-button" style={{ fontSize: fontSizes.button }} onClick={handleBackToView}>{cancelLabel}</button>
          <button type="button" className="contact-details-modal-primary-button" style={{ fontSize: fontSizes.button }} onClick={handleSubmit} disabled={submitDisabled}>{t.CONTACT_DETAILS_CONFIRM}</button>
        </div>
      </div>
    );

    const editContentStep = (
      <>
        <div className="step-header">
          <h2>{editMode === 'email' ? t.CONTACT_DETAILS_CHANGE_EMAIL : t.CONTACT_DETAILS_CHANGE_MOBILE}</h2>
        </div>
        {error && (
          <Alert
            message={error}
            type="error"
            role="alert"
            aria-live="assertive"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div className="contact-details-step-edit">
          <label className="contact-details-step-edit-label">{fieldLabel}</label>
          {isMobileEdit ? (
            <>
              <PhoneInput
                international
                defaultCountry="DE"
                value={newValue}
                onChange={(val) => setNewValue(val || '')}
                placeholder={placeholder}
                className={"contact-details-phone-input contact-details-step-phone" + (phoneRegexError ? " contact-details-phone-input-error" : "")}
                numberInputProps={{
                  className: "contact-details-step-input",
                  autoFocus: true,
                }}
              />
              {phoneRegexError && (
                <p className="contact-details-step-error contact-details-step-field-error">{phoneRegexError}</p>
              )}
            </>
          ) : (
            <input
              type={inputType}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={placeholder}
              className="contact-details-step-input"
              autoFocus
            />
          )}
        </div>
        <div className="contract-step-actions">
          <button type="button" className="button secondary" onClick={handleBackToView}>{cancelLabel}</button>
          <button type="button" className="button primary" onClick={handleSubmit} disabled={submitDisabled}>{t.CONTACT_DETAILS_CONFIRM}</button>
        </div>
      </>
    );

    return asStep ? (
      <div className="step-container contact-details-step">{editContentStep}</div>
    ) : (
      <div className="contact-details-modal-overlay" role="dialog" aria-modal="true">{editContentModal}</div>
    );
  }

  const viewContentModal = (
    <div className="contact-details-modal">
      <h2 className="contact-details-modal-title" style={{ fontSize: fontSizes.title }}>{t.CONTACT_DETAILS_TITLE}</h2>
      <p className="contact-details-modal-description" style={{ fontSize: fontSizes.text }}>{t.CONTACT_DETAILS_DESCRIPTION}</p>
      <div className="contact-details-modal-info-section">
        <div className="contact-details-modal-info-row">
          <span className="contact-details-modal-info-label" style={{ fontSize: fontSizes.text }}>{t.PARTNER_NAME}</span>
          <span className="contact-details-modal-info-value" style={{ fontSize: fontSizes.text }}>{originalData.firstName} {originalData.lastName}</span>
        </div>
        <div className="contact-details-modal-info-row">
          <span className="contact-details-modal-info-label" style={{ fontSize: fontSizes.text }}>{t.USER_EMAIL}</span>
          <div className="contact-details-modal-info-value-container">
            <span className="contact-details-modal-info-value" style={{ fontSize: fontSizes.text }}>{originalData.email || "-"}</span>
            <button className="contact-details-modal-change-link" style={{ fontSize: fontSizes.text }} onClick={() => handleChangeClick('email')}>{originalData.email ? t.CONTACT_DETAILS_CHANGE_EMAIL : t.CONTACT_DETAILS_ADD_EMAIL}</button>
          </div>
        </div>
        <div className="contact-details-modal-info-row">
          <span className="contact-details-modal-info-label" style={{ fontSize: fontSizes.text }}>{t.CONTACT_DETAILS_MOBILE}</span>
          <div className="contact-details-modal-info-value-container">
            <span className="contact-details-modal-info-value" style={{ fontSize: fontSizes.text }}>{originalData.mobile || "-"}</span>
            <button className="contact-details-modal-change-link" style={{ fontSize: fontSizes.text }} onClick={() => handleChangeClick('mobile')}>{originalData.mobile ? t.CONTACT_DETAILS_CHANGE_MOBILE : t.CONTACT_DETAILS_ADD_MOBILE}</button>
          </div>
        </div>
      </div>
      <div className="contact-details-modal-button-container">
        <button className="contact-details-modal-secondary-button" style={{ fontSize: fontSizes.button }} onClick={handleClose}>{cancelLabel}</button>
        <button className="contact-details-modal-primary-button" style={{ fontSize: fontSizes.button }} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  );

  const viewContentStep = (
    <>
      <div className="step-header">
        <h2>{t.CONTACT_DETAILS_TITLE}</h2>
        <p>{t.CONTACT_DETAILS_DESCRIPTION}</p>
      </div>
      <div className="contact-details-step-info">
        <div className="contact-details-step-row">
          <span className="contact-details-step-label">{t.PARTNER_NAME}</span>
          <span className="contact-details-step-value">{originalData.firstName} {originalData.lastName}</span>
        </div>
        <div className="contact-details-step-row">
          <span className="contact-details-step-label">{t.USER_EMAIL}</span>
          <div className="contact-details-step-value-wrap">
            <span className="contact-details-step-value">{originalData.email || "-"}</span>
            <button type="button" className="contact-details-step-change-link" onClick={() => handleChangeClick('email')}>{originalData.email ? t.CONTACT_DETAILS_CHANGE_EMAIL : t.CONTACT_DETAILS_ADD_EMAIL}</button>
          </div>
        </div>
        <div className="contact-details-step-row">
          <span className="contact-details-step-label">{t.CONTACT_DETAILS_MOBILE}</span>
          <div className="contact-details-step-value-wrap">
            <span className="contact-details-step-value">{originalData.mobile || "-"}</span>
            <button type="button" className="contact-details-step-change-link" onClick={() => handleChangeClick('mobile')}>{originalData.mobile ? t.CONTACT_DETAILS_CHANGE_MOBILE : t.CONTACT_DETAILS_ADD_MOBILE}</button>
          </div>
        </div>
      </div>
      <div className="contract-step-actions">
        <button type="button" className="button secondary" onClick={handleClose}>{cancelLabel}</button>
        <button type="button" className="button primary" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </>
  );

  return asStep ? (
    <div className="step-container contact-details-step">{viewContentStep}</div>
  ) : (
    <div className="contact-details-modal-overlay" role="dialog" aria-modal="true">{viewContentModal}</div>
  );
};

export default inject("contractStore")(observer(ContactDetailsModal));
